import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, X } from "lucide-react";
import { objectClasses } from "@the-real-insight/tri-model";

import { ResolutionCandidate } from "../../DocumentAnalysisAPI";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * The candidates as a table, so a person can choose between them.
 *
 * Which columns a table of `LegalPerson`s has is not a decision this file may take. It is written
 * down in tri-model — which fields the class declares, what they are called in German, which of
 * them the model considers identifying — and read from there. A hand-kept column list would be one
 * more place to update when the model learns a field, and it would be wrong within a month.
 *
 * Choosing the columns is nevertheless not the same as listing every field. Three companies of the
 * same name are told apart by what *differs* between them, and a table whose every row reads
 * "Müller Verwaltungs GmbH · GmbH · Brandenburg" answers nothing. So the fields divide in two:
 *
 *  - what every candidate says alike — including, almost always, the value that found them. Stated
 *    once above the table, because repeating it in every row spends the width the distinguishing
 *    fields need and tells nobody anything.
 *  - what differs — the columns, with the fields the matched key compared first, since those are
 *    the reason these rows are on screen at all.
 *
 * The split is the only data-driven step: the model says what a field is, the candidates say which
 * of them is worth a column.
 */

/** How many value columns a reader can take in beside the score. */
const MAX_COLUMNS = 5;

interface FieldMeta {
  name: string;
  label: string;
}

/** The declared, readable fields of a class, by the name the model gives them in German. */
const fieldsOf = (className: string | null): FieldMeta[] => {
  if (!className) return [];

  const entityClass: any = (objectClasses as any[]).find(
    (entry: any) => entry?.type?.name === className,
  );

  return ((entityClass?.fields ?? []) as any[])
    .filter((field: any) => field?.name && !field.internal && !field.derived)
    .map((field: any) => ({
      name: field.name,
      label: field.label?.de ?? field.label?.en ?? field.name,
    }));
};

/**
 * What the candidates agree on about *how* they were matched.
 *
 * In the ordinary ambiguous case every candidate was found by the same key on the same fields —
 * five companies of one name, all matched on the name. Printing that in every row says the same
 * thing five times and pushes the values that actually differ out of view. It is only per-row
 * information when the rows genuinely differ, which happens when an abstract type was searched
 * across its concrete classes.
 */
const sharedMatch = (
  candidates: ResolutionCandidate[],
): { keyName: string; matchedOn: string[] } | null => {
  if (candidates.length === 0) return null;

  const signature = (candidate: ResolutionCandidate) =>
    `${candidate.keyName}|${[...candidate.matchedOn].sort().join(",")}`;

  const first = signature(candidates[0]);

  return candidates.every((candidate) => signature(candidate) === first)
    ? { keyName: candidates[0].keyName, matchedOn: candidates[0].matchedOn }
    : null;
};

/** The names the class's business keys compare — what made these candidates candidates. */
const keyFieldsOf = (className: string | null): string[] => {
  if (!className) return [];

  const entityClass: any = (objectClasses as any[]).find(
    (entry: any) => entry?.type?.name === className,
  );

  return ((entityClass?.businessKeys ?? []) as any[]).reduce<string[]>(
    (all, key: any) =>
      all.concat(
        (key?.fields ?? []).map((field: any) => String(field?.name ?? '').split(".")[0]),
      ),
    [],
  );
};

/**
 * Which fields the candidates agree on, and which tell them apart.
 *
 * A column whose value is the same in every row cannot help anyone choose — and the name that
 * *found* three companies is exactly such a column. Repeating "Müller Verwaltungs GmbH" three times
 * fills the width the distinguishing fields need and says nothing. So it is lifted out of the table
 * and stated once above it, where it reads as what it is: the thing they have in common.
 *
 * What is left are the fields that differ, ordered by whether the matched key compared them.
 */
export const splitColumns = (
  className: string | null,
  candidates: ResolutionCandidate[],
  limit = MAX_COLUMNS,
): { shared: Array<FieldMeta & { value: string }>; columns: FieldMeta[] } => {
  const declared = fieldsOf(className);
  const keyFields = keyFieldsOf(className);

  const valueOf = (candidate: ResolutionCandidate, field: string) =>
    String((candidate.values ?? {})[field] ?? "");

  const filled = (field: string) => candidates.some((candidate) => valueOf(candidate, field) !== "");

  const sameEverywhere = (field: string) => {
    const seen = candidates.map((candidate) => valueOf(candidate, field));
    return seen.every((value) => value === seen[0] && value !== "");
  };

  // With a single candidate nothing is "in common" — everything it says is about it alone.
  const shared =
    candidates.length > 1
      ? declared
          .filter((field) => filled(field.name) && sameEverywhere(field.name))
          .map((field) => ({ ...field, value: valueOf(candidates[0], field.name) }))
      : [];

  const varying = declared.filter(
    (field) => filled(field.name) && !shared.some((entry) => entry.name === field.name),
  );

  const ordered = [
    ...varying.filter((field) => keyFields.indexOf(field.name) >= 0),
    ...varying.filter((field) => keyFields.indexOf(field.name) < 0),
  ];

  const columns: FieldMeta[] = [];
  ordered.forEach((field) => {
    if (columns.length >= limit) return;
    if (columns.some((entry) => entry.name === field.name)) return;
    columns.push(field);
  });

  return { shared, columns };
};

interface Properties {
  className: string | null;
  candidates: ResolutionCandidate[];
  /** Absent while a decision cannot be recorded — then the table is a listing, not a question. */
  onBind?: (candidate: ResolutionCandidate) => void;
  onReject?: () => void;
  busy?: boolean;
  /** What is already decided, so a repeated view shows the choice rather than asking again. */
  boundObjectId?: string | null;
  /**
   * Fields the caller has already stated above the table.
   *
   * The dialog names what the *document* says before showing the candidates; repeating the same
   * field as "what they have in common" says it twice in four lines.
   */
  statedFields?: string[];
}

export default function CandidateTable({
  className,
  candidates,
  onBind,
  onReject,
  busy,
  boundObjectId,
  statedFields,
}: Properties) {
  const { t } = useTranslation();

  const { shared, columns } = useMemo(() => {
    const split = splitColumns(className, candidates);

    return {
      ...split,
      shared: split.shared.filter((field) => (statedFields ?? []).indexOf(field.name) < 0),
    };
  }, [className, candidates, statedFields]);

  const common = useMemo(() => sharedMatch(candidates), [candidates]);

  if (candidates.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      {/* What they have in common, said once. Among it is almost always the value that found them. */}
      {shared.length > 0 ? (
        <div className="mb-2 rounded-md border bg-muted/40 px-3 py-2">
          <div className="mb-0.5 text-[11px] font-medium text-muted-foreground">
            {t("documentAnalysisPanel.matching.sharedValues")}
          </div>
          <dl className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-x-3 text-xs">
            {shared.map((field) => (
              <div key={field.name} className="contents">
                <dt className="truncate text-muted-foreground" title={field.name}>
                  {field.label}
                </dt>
                <dd className="min-w-0 break-words font-medium">{field.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      {/* How they were all matched — said once, because saying it per row says it five times. */}
      {common ? (
        <p className="mb-2 text-[11px] text-muted-foreground">
          {describeMatch(className, common.keyName, common.matchedOn, t)}
        </p>
      ) : null}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            {columns.map((column) => (
              <TableHead key={column.name} className="whitespace-nowrap text-xs">
                {column.label}
              </TableHead>
            ))}
            <TableHead className="whitespace-nowrap text-xs" title={t("documentAnalysisPanel.matching.matchColumnHint")}>
              {t("documentAnalysisPanel.matching.matchColumn")}
            </TableHead>
            {onBind ? <TableHead className="w-[120px]" /> : null}
          </TableRow>
        </TableHeader>

        <TableBody>
          {candidates.map((candidate, index) => {
            const chosen = boundObjectId === candidate.objectId;

            return (
              <TableRow key={candidate.objectId} className={chosen ? "bg-emerald-50 dark:bg-emerald-950/40" : ""}>
                <TableCell className="text-xs text-muted-foreground">{index + 1}</TableCell>

                {columns.map((column) => (
                  <TableCell key={column.name} className="max-w-[220px] truncate text-xs" title={String((candidate.values ?? {})[column.name] ?? "")}>
                    {String((candidate.values ?? {})[column.name] ?? "—")}
                  </TableCell>
                ))}

                <TableCell className="whitespace-nowrap text-xs">
                  <span className="font-medium">{Math.round(candidate.score * 100)} %</span>

                  {/* Only where the rows differ in it — otherwise it stands once above the table. */}
                  {!common ? (
                    <span className="ml-1 text-muted-foreground">
                      {describeMatch(className, candidate.keyName, candidate.matchedOn, t)}
                    </span>
                  ) : null}

                  {candidate.constraintViolations.length > 0 ? (
                    <span className="ml-1 inline-flex items-center gap-1 text-destructive">
                      <AlertTriangle className="h-3 w-3" aria-hidden />
                      {candidate.constraintViolations.join(", ")}
                    </span>
                  ) : null}
                </TableCell>

                {onBind ? (
                  <TableCell>
                    <Button
                      size="sm"
                      variant={chosen ? "default" : "outline"}
                      disabled={busy}
                      onClick={() => onBind(candidate)}
                    >
                      <Check className="mr-1 h-3 w-3" aria-hidden />
                      {chosen
                        ? t("documentAnalysisPanel.matching.chosen")
                        : t("documentAnalysisPanel.matching.choose")}
                    </Button>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* "None of these" is an answer, and a useful one: it says the estate does not hold the thing,
          which is what `new` means. Without it the only way to disagree is to say nothing. */}
      {onReject ? (
        <div className="mt-1">
          <Button variant="ghost" size="sm" disabled={busy} onClick={onReject}>
            <X className="mr-1 h-3 w-3" aria-hidden />
            {t("documentAnalysisPanel.matching.noneOfThese")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Verglichen über Firmenname" — the key that matched, in the words the model uses for its fields.
 *
 * The key's own name (`companyName`) is dropped when it says nothing beyond the field list: a key
 * called after the single field it compares reads as that field twice.
 */
const describeMatch = (
  className: string | null,
  keyName: string,
  matchedOn: string[],
  // i18next's `t` is generic over the key and does not narrow to `string`; the values behind these
  // keys are strings, and `String` says so without fighting the type.
  translate: (key: string, options?: any) => any,
): string => {
  const t = (key: string, options?: any) => String(translate(key, options));

  const labels = fieldsOf(className);

  // `matchedOn` names the fields that agreed; the key's own identifier is not shown beside them.
  const named = matchedOn
    .map((entry) => {
      // A trailing `~` marks a fuzzy agreement and is part of the answer, not of the field name.
      const fuzzy = entry.endsWith("~");
      const field = fuzzy ? entry.slice(0, -1) : entry;
      const label = labels.find((candidate) => candidate.name === field)?.label ?? field;

      return fuzzy ? `${label} (ungefähr)` : label;
    })
    .join(", ");

  if (named.length === 0) return t("documentAnalysisPanel.matching.comparedByKey", { key: keyName });

  return t("documentAnalysisPanel.matching.comparedBy", { fields: named });
};
